/**
 * TicketTimelineV2 - Timeline unificada do ticket (Spec A Task 8).
 *
 * Consome `useTicketTimeline` (infinite query por cursor sobre
 * `GET /tickets/:id/timeline`) e renderiza cada item pelo discriminante
 * `kind` — `'activity'` via `TimelineActivityItem`, `'email'` via
 * `TimelineEmailItem`. Backend ordena DESC (mais recente primeiro, ver
 * `timeline-service.ts`) — mantemos essa ordem por padrão (feed tipo
 * activity-log; "Carregar mais antigas" busca a página seguinte via cursor).
 *
 * Esta task só constrói o componente + data layer. INTEGRAÇÃO na
 * `TicketDetailPage` (troca da aba "Atividades"/`TicketTimeline` legado,
 * decisão de ordem de exibição tipo-conversa vs. feed, scroll/composer) é da
 * Task 9 — ver relatório da Task 8 para a API completa e opções de plug.
 *
 * IMPORTANTE: hoje só existem itens `kind: 'activity'` (o Plano B pluga
 * `kind: 'email'` no backend sem mudar este componente).
 */
import React from 'react';
import { useTicketTimeline } from '../../hooks/useTickets';
import { TimelineActivityItem } from './TimelineActivityItem';
import { TimelineEmailItem } from './TimelineEmailItem';

export interface TicketTimelineV2Props {
  /** ID do ticket (string, não ObjectId). */
  ticketId: string;
  /** Inclui notas internas na timeline. Default `true`. */
  includeInternal?: boolean;
  /** Tamanho de página do cursor (backend default 30). */
  limit?: number;
  className?: string;
}

export const TicketTimelineV2: React.FC<TicketTimelineV2Props> = ({
  ticketId,
  includeInternal = true,
  limit,
  className,
}) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTicketTimeline(ticketId, { includeInternal, limit });

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div data-testid="ticket-timeline-loading" className="space-y-4 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full" style={{ backgroundColor: 'var(--theme-border)' }} />
              <div className="flex-1 space-y-2">
                <div className="h-4 rounded w-1/4" style={{ backgroundColor: 'var(--theme-border)' }} />
                <div className="h-16 rounded" style={{ backgroundColor: 'var(--theme-border)' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="ticket-timeline-error" className="flex items-center justify-center h-48">
        <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
          Não foi possível carregar a timeline do ticket.
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="ticket-timeline-empty" className="flex items-center justify-center h-48">
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhuma atividade registrada
        </p>
      </div>
    );
  }

  // Mockup (ticket-detail.html .timeline): ordem CRONOLÓGICA ascendente (mais
  // antigo → mais novo) e "Carregar mais antigas" no TOPO. Backend entrega DESC,
  // então invertemos para exibir.
  const orderedItems = [...items].reverse();

  return (
    <div
      data-testid="ticket-timeline-root"
      className={['flex flex-col gap-4 p-4', className].filter(Boolean).join(' ')}
    >
      {hasNextPage && (
        <button
          type="button"
          onClick={() => {
            void fetchNextPage();
          }}
          disabled={isFetchingNextPage}
          data-testid="ticket-timeline-load-more"
          className="self-center px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-button-ghost)' }}
        >
          {isFetchingNextPage ? 'Carregando…' : 'Carregar mais antigas'}
        </button>
      )}

      {orderedItems.map((item) =>
        item.kind === 'email' ? (
          <TimelineEmailItem key={`${item.kind}-${item.id}`} item={item} />
        ) : (
          <TimelineActivityItem key={`${item.kind}-${item.id}`} item={item} />
        ),
      )}
    </div>
  );
};

TicketTimelineV2.displayName = 'TicketTimelineV2';
